import { Address } from '@/types/request';
import axios from 'axios';

/**
 * @author <Aniket.P>
 * @description Map utils
 * @copyright Supra International, inc
 */
export class MapUtils {

    /**
     * @description
     *  Parse google address into address object. Property definitions can be found here:
     *  https://googlemaps.github.io/google-maps-services-java/v0.1.3/javadoc/com/google/maps/model/AddressType.html
     * @param googleAddress
     * @returns Address
     */
    public static parseGoogleAddress(googleAddress: any): Address {
        const place = googleAddress.getPlace();
        const addressComponenets = place.address_components;
        const zipComponent = this.findComponent(addressComponenets, 'postal_code');
        const countryComponent = this.findComponent(addressComponenets, 'country');
        const localityComponent = this.findComponent(addressComponenets, 'locality')
            || this.findComponent(addressComponenets, 'postal_town') ||
            this.findComponent(addressComponenets, 'neighborhood') ||
            this.findComponent(addressComponenets, 'administrative_area_level_3');
        const stateComponent = this.findComponent(addressComponenets, 'administrative_area_level_1');
        const countyComponent = this.findComponent(addressComponenets, 'administrative_area_level_2');
        const subpremiseComponent = this.findComponent(addressComponenets, 'subpremise');
        const subLocalityComponent = this.findComponent(addressComponenets, 'sublocality');
        const address: Address = {
            fullAddress: place.formatted_address,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            zip: zipComponent ? zipComponent.long_name : undefined,
            country: countryComponent ? countryComponent.long_name : undefined,
            city: localityComponent ? localityComponent.long_name : undefined,
            state: stateComponent ? stateComponent.long_name : undefined,
            subLocality: subLocalityComponent ? subLocalityComponent.long_name : undefined,
            county: countyComponent ? countyComponent.long_name : undefined,
            locality: subpremiseComponent ? subpremiseComponent.long_name : undefined,
        };
        return address;
    }

    public static async getAddressByCoordinates(lat: number, lng: number): Promise<Address> {
        const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBg5K7RTQ8Ppu3Y9qFbuWgdQMo8EC_alCU`;
        try {
            const response = await axios.get(apiUrl);
            const address: Address = {
                fullAddress: response.data.results[0].formatted_address,
                country: response.data.results[0].address_components[6].long_name,
                county: response.data.results[0].address_components[4].long_name,
                city: response.data.results[0].address_components[3].long_name,
                zip: response.data.results[0].address_components[7].long_name,
                state: response.data.results[0].address_components[5].long_name,
                locality: response.data.results[0].address_components[4].long_name,
                latitude: lat,
                longitude: lng,
            };
            return address;
        } catch (error) {
            throw error;
        }
    }

    public static async getAddressByStreet(streetAddress: string): Promise<Address> {
        const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${streetAddress}&key=AIzaSyBg5K7RTQ8Ppu3Y9qFbuWgdQMo8EC_alCU`;
        try {
            const response = await axios.get(apiUrl);
            const address: Address = {
                fullAddress: response.data.results[0].formatted_address,
                country: response.data.results[0].address_components[6].long_name,
                county: response.data.results[0].address_components[4].long_name,
                city: response.data.results[0].address_components[3].long_name,
                zip: response.data.results[0].address_components[7].long_name,
                state: response.data.results[0].address_components[5].long_name,
                locality: response.data.results[0].address_components[4].long_name,
                latitude: response.data.results[0].geometry.location.lat,
                longitude: response.data.results[0].geometry.location.lng,
            };
            return address;
        } catch (error) {
            throw error;
        }
    }

    private static findComponent(addressComponenets: any[], componentId: string): any {
        let result;
        addressComponenets.forEach((addressComponenet) => {
            if (addressComponenet.types.indexOf(componentId) >= 0) {
                result = addressComponenet;
            }
        });
        return result;
    }

}
